import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';
import { smart_meters_base_url, customer_account_base_url } from "../../../../../../utils/urls";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const SmartMetersTab = () => {
    const [meters, setMeters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState(10);
    const navigate = useNavigate();
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const [showConsumptionModal, setShowConsumptionModal] = useState(false);
    const [consumptionFormat, setConsumptionFormat] = useState('pdf');
    const [consumptionLoading, setConsumptionLoading] = useState(false);
    const [selectedMonths, setSelectedMonths] = useState([]);

    const [showMetersExportModal, setShowMetersExportModal] = useState(false);
    const [metersExportFormat, setMetersExportFormat] = useState('pdf');
    const [metersExportLoading, setMetersExportLoading] = useState(false);

    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchMeters(selectedFacilityId);
        }
    }, [selectedFacilityId]);

    const fetchMeters = async (currentFacilityId) => {
        try {
            setLoading(true);
            const idToUse = currentFacilityId || selectedFacilityId;
            if (!idToUse) throw new Error('No facility ID available');

            const response = await makeRequest2(
                `${smart_meters_base_url}/${idToUse}`,
                'GET',
                null
            );

            if (response.success) {
                const metersData = Array.isArray(response.data.meters)
                    ? response.data.meters
                    : [];
                const sortedMeters = metersData.sort((a, b) => {
                    if (a.installationStatus === 'installed' && b.installationStatus !== 'installed') return -1;
                    if (a.installationStatus !== 'installed' && b.installationStatus === 'installed') return 1;
                    return 0;
                });
                setMeters(sortedMeters);
            } else {
                toastify('Failed to fetch water meters', 'error');
            }
        } catch (error) {
            toastify('Error fetching water meters', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getRecentMonths = (count = 24) => {
        const months = [];
        const now = new Date();
        for (let i = 0; i < count; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            months.push({ value, label });
        }
        return months;
    };

    const toggleMonth = (monthValue) => {
        setSelectedMonths(prev =>
            prev.includes(monthValue)
                ? prev.filter(m => m !== monthValue)
                : [...prev, monthValue]
        );
    };

    const formatYearMonth = (ym) => {
        if (!ym) return 'N/A';
        const [year, month] = ym.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, 1)
            .toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value ?? 0);

    const fetchFacilityConsumption = async () => {
        if (selectedMonths.length === 0) {
            toastify('Please select at least one month', 'warning');
            return null;
        }
        try {
            setConsumptionLoading(true);
            const monthsParam = [...selectedMonths].sort().join(',');
            const response = await makeRequest2(
                `${smart_meters_base_url}/facility-monthly-consumption/${selectedFacilityId}?months=${monthsParam}`,
                'GET',
                null
            );
            if (response.success) return response.data;
            toastify('Failed to fetch consumption data', 'error');
            return null;
        } catch (error) {
            toastify('Error fetching consumption data', 'error');
            return null;
        } finally {
            setConsumptionLoading(false);
        }
    };

    const fetchFinancialSummary = async () => {
        try {
            const monthsParam = [...selectedMonths].sort().join(',');
            const response = await makeRequest2(
                `${customer_account_base_url}/facility-financial-summary/${selectedFacilityId}?months=${monthsParam}`,
                'GET',
                null
            );
            if (response.success) return response.data;
            return null;
        } catch (error) {
            console.warn('Financial summary unavailable:', error);
            return null;
        }
    };

    // ── Export Meters List as PDF (3 columns only) ────────────────
    const handleExportMetersPDF = () => {
        setMetersExportLoading(true);
        try {
            const doc = new jsPDF('p', 'mm', 'a4'); // portrait is fine for 3 columns
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const primaryColor = [30, 64, 175];
            const lightGray = [243, 244, 246];
            const darkGray = [55, 65, 81];

            // Header
            doc.setFillColor(...primaryColor);
            doc.rect(0, 0, pageWidth, 45, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('FACILITY WATER METERS LIST', pageWidth / 2, 18, { align: 'center' });
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            const generatedDate = new Date().toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
            doc.text(`Generated: ${generatedDate}`, pageWidth / 2, 30, { align: 'center' });

            doc.setTextColor(...darkGray);
            doc.setFontSize(9);
            doc.text(`Total Meters: ${filteredMeters.length}`, 14, 53);

            // Summary box
            const summaryY = 58;
            doc.setFillColor(235, 245, 255);
            doc.roundedRect(14, summaryY, pageWidth - 28, 12, 2, 2, 'F');
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...primaryColor);
            doc.text(
                `Total Meters: ${filteredMeters.length}`,
                pageWidth / 2, summaryY + 7.5, { align: 'center' }
            );

            // Table — only 3 columns
            const tableData = filteredMeters.map((meter, i) => [
                i + 1,
                meter.meterNumber || 'N/A',
                meter.accountNumber || 'N/A',
                meter.UnitInfo?.name || 'N/A',
            ]);

            doc.autoTable({
                startY: summaryY + 18,
                head: [['#', 'Meter Serial Number', 'Account Number', 'Unit Name']],
                body: tableData,
                theme: 'striped',
                headStyles: {
                    fillColor: primaryColor, textColor: 255,
                    fontSize: 9, fontStyle: 'bold', halign: 'center'
                },
                bodyStyles: { fontSize: 9, textColor: darkGray },
                alternateRowStyles: { fillColor: lightGray },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center' },
                    1: { cellWidth: 60 },
                    2: { cellWidth: 50, halign: 'center' },
                    3: { cellWidth: 'auto' },
                },
                margin: { left: 14, right: 14 },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                    doc.setFontSize(8);
                    doc.setTextColor(130);
                    doc.setFont('helvetica', 'italic');
                    doc.text('This is a system generated report', pageWidth / 2, pageHeight - 20, { align: 'center' });
                    doc.setFont('helvetica', 'normal');
                    doc.text('Developed and Maintained by Payserve', pageWidth / 2, pageHeight - 15, { align: 'center' });
                    doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
                }
            });

            const timestamp = new Date().toISOString().split('T')[0];
            doc.save(`Facility_Meters_List_${timestamp}.pdf`);
            setShowMetersExportModal(false);
            toastify('Meters list exported successfully', 'success');
        } catch (err) {
            console.error(err);
            toastify('Error exporting meters list', 'error');
        } finally {
            setMetersExportLoading(false);
        }
    };

    // ── Export Meters List as Excel (3 columns only) ──────────────
    const handleExportMetersExcel = () => {
        setMetersExportLoading(true);
        try {
            const excelData = filteredMeters.map((meter, i) => ({
                'No.': i + 1,
                'Meter Serial Number': meter.meterNumber || 'N/A',
                'Account Number': meter.accountNumber || 'N/A',
                'Unit Name': meter.UnitInfo?.name || 'N/A',
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [
                { wch: 5 },
                { wch: 28 },
                { wch: 22 },
                { wch: 32 },
            ];

            // Info sheet
            const infoSheet = XLSX.utils.json_to_sheet([
                { 'Info': 'Facility Water Meters List' },
                { 'Info': `Generated: ${new Date().toLocaleString()}` },
                { 'Info': `Total Meters: ${filteredMeters.length}` },
            ]);

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Meters List');
            XLSX.utils.book_append_sheet(wb, infoSheet, 'Report Info');

            const timestamp = new Date().toISOString().split('T')[0];
            XLSX.writeFile(wb, `Facility_Meters_List_${timestamp}.xlsx`);
            setShowMetersExportModal(false);
            toastify('Meters list exported successfully', 'success');
        } catch (err) {
            console.error(err);
            toastify('Error exporting meters list', 'error');
        } finally {
            setMetersExportLoading(false);
        }
    };

    const handleExportMeters = () => {
        if (metersExportFormat === 'pdf') handleExportMetersPDF();
        else handleExportMetersExcel();
    };

    const handleExportPDF = async () => {
        const data = await fetchFacilityConsumption();
        if (!data || !data.monthlyTotals?.length) {
            toastify('No consumption data found for the selected months', 'warning');
            return;
        }

        const financialResponse = await fetchFinancialSummary();
        const financialData = financialResponse?.data;

        const { monthlyTotals, grandTotalConsumption, totalMeters } = data;
        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const successColor = [22, 163, 74];
        const dangerColor = [220, 38, 38];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];
        const activeColor = [6, 95, 70];
        const inactiveColor = [127, 29, 29];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('FACILITY WATER CONSUMPTION REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const periodLabel = monthlyTotals.map(r => formatYearMonth(r.yearMonth)).join(', ');
        const splitPeriod = doc.splitTextToSize(`Period: ${periodLabel}`, pageWidth - 28);
        doc.text(splitPeriod, pageWidth / 2, 29, { align: 'center' });

        doc.setTextColor(...darkGray);
        doc.setFontSize(9);
        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
        doc.text(`Generated: ${generatedDate}`, 14, 53);
        doc.text(`Total Meters in Facility: ${totalMeters}`, pageWidth - 14, 53, { align: 'right' });

        const summaryY = 58;
        doc.setFillColor(235, 245, 255);
        doc.roundedRect(14, summaryY, pageWidth - 28, 12, 2, 2, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(
            `Grand Total: ${parseFloat(grandTotalConsumption).toFixed(2)} m³   |   Months: ${monthlyTotals.length}`,
            pageWidth / 2, summaryY + 7.5, { align: 'center' }
        );

        const totalInitial = monthlyTotals.reduce((s, r) => s + r.totalInitialReading, 0);
        const totalFinal = monthlyTotals.reduce((s, r) => s + r.totalFinalReading, 0);

        const tableData = monthlyTotals.map((row, i) => [
            i + 1,
            formatYearMonth(row.yearMonth),
            row.yearMonth,
            row.meterCount,
            row.totalInitialReading.toFixed(2),
            row.totalFinalReading.toFixed(2),
            row.totalConsumption.toFixed(2)
        ]);
        tableData.push([
            '', 'TOTAL', '', '',
            totalInitial.toFixed(2),
            totalFinal.toFixed(2),
            parseFloat(grandTotalConsumption).toFixed(2)
        ]);

        doc.autoTable({
            startY: summaryY + 18,
            head: [['#', 'Month', 'Year-Month', 'Meters', 'Initial (m³)', 'Final (m³)', 'Consumption (m³)']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 9, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 40 },
                2: { cellWidth: 22, halign: 'center' },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 28, halign: 'right' },
                5: { cellWidth: 28, halign: 'right' },
                6: { cellWidth: 28, halign: 'right', fontStyle: 'bold' }
            },
            margin: { left: 14, right: 14 },
            didParseCell: (hook) => {
                if (hook.section === 'body' && hook.row.index === tableData.length - 1) {
                    hook.cell.styles.fontStyle = 'bold';
                    hook.cell.styles.fillColor = [220, 230, 255];
                    hook.cell.styles.textColor = primaryColor;
                }
            },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 20, { align: 'center' });
                doc.setFont('helvetica', 'normal');
                doc.text('Developed and Maintained by Payserve', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }
        });

        if (financialData && financialData.monthlySummary?.length) {
            doc.addPage();
            const finStartY = 20;
            doc.setTextColor(...darkGray);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Financial Summary — Active vs Inactive Accounts', 14, finStartY);

            doc.setFillColor(240, 253, 244);
            doc.roundedRect(14, finStartY + 6, 80, 9, 2, 2, 'F');
            doc.setFillColor(255, 241, 242);
            doc.roundedRect(100, finStartY + 6, 80, 9, 2, 2, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...activeColor);
            doc.text(`Active Accounts: ${financialData.totalActiveAccounts}`, 54, finStartY + 11.5, { align: 'center' });
            doc.setTextColor(...inactiveColor);
            doc.text(`Inactive Accounts: ${financialData.totalInactiveAccounts}`, 140, finStartY + 11.5, { align: 'center' });

            const { monthlySummary } = financialData;
            const totals = monthlySummary.reduce((acc, row) => ({
                activeBF: acc.activeBF + (row.active?.balanceBF || 0),
                activeCredits: acc.activeCredits + (row.active?.creditsInMonth || 0),
                activeDebits: acc.activeDebits + (row.active?.debitsInMonth || 0),
                activeClosing: acc.activeClosing + (row.active?.closingBalance || 0),
                inactiveBF: acc.inactiveBF + (row.inactive?.balanceBF || 0),
                inactiveCredits: acc.inactiveCredits + (row.inactive?.creditsInMonth || 0),
                inactiveDebits: acc.inactiveDebits + (row.inactive?.debitsInMonth || 0),
                inactiveClosing: acc.inactiveClosing + (row.inactive?.closingBalance || 0),
            }), {
                activeBF: 0, activeCredits: 0, activeDebits: 0, activeClosing: 0,
                inactiveBF: 0, inactiveCredits: 0, inactiveDebits: 0, inactiveClosing: 0,
            });

            const finTableData = monthlySummary.map((row, i) => [
                i + 1,
                formatYearMonth(row.yearMonth),
                formatCurrency(row.active?.balanceBF || 0),
                formatCurrency(row.active?.creditsInMonth || 0),
                formatCurrency(row.active?.debitsInMonth || 0),
                formatCurrency(row.active?.closingBalance || 0),
                formatCurrency(row.inactive?.balanceBF || 0),
                formatCurrency(row.inactive?.creditsInMonth || 0),
                formatCurrency(row.inactive?.debitsInMonth || 0),
                formatCurrency(row.inactive?.closingBalance || 0),
            ]);
            finTableData.push([
                '', 'TOTAL',
                formatCurrency(totals.activeBF), formatCurrency(totals.activeCredits),
                formatCurrency(totals.activeDebits), formatCurrency(totals.activeClosing),
                formatCurrency(totals.inactiveBF), formatCurrency(totals.inactiveCredits),
                formatCurrency(totals.inactiveDebits), formatCurrency(totals.inactiveClosing),
            ]);

            doc.autoTable({
                startY: finStartY + 18,
                head: [
                    [
                        { content: '#', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
                        { content: 'Month', rowSpan: 2, styles: { valign: 'middle' } },
                        { content: `Active Accounts (${financialData.totalActiveAccounts})`, colSpan: 4, styles: { halign: 'center', fillColor: activeColor, textColor: 255 } },
                        { content: `Inactive Accounts (${financialData.totalInactiveAccounts})`, colSpan: 4, styles: { halign: 'center', fillColor: inactiveColor, textColor: 255 } },
                    ],
                    ['Bal B/F', 'Credits', 'Debits', 'Closing Bal', 'Bal B/F', 'Credits', 'Debits', 'Closing Bal'],
                ],
                body: finTableData,
                theme: 'striped',
                headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'right' },
                bodyStyles: { fontSize: 8, textColor: darkGray, halign: 'right' },
                alternateRowStyles: { fillColor: lightGray },
                columnStyles: {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 38, halign: 'left' },
                    2: { cellWidth: 28 }, 3: { cellWidth: 28, textColor: successColor },
                    4: { cellWidth: 28, textColor: dangerColor }, 5: { cellWidth: 28, fontStyle: 'bold' },
                    6: { cellWidth: 28 }, 7: { cellWidth: 28, textColor: successColor },
                    8: { cellWidth: 28, textColor: dangerColor }, 9: { cellWidth: 28, fontStyle: 'bold' },
                },
                margin: { left: 14, right: 14 },
                didParseCell: (hook) => {
                    if (hook.section === 'body' && hook.row.index === finTableData.length - 1) {
                        hook.cell.styles.fontStyle = 'bold';
                        hook.cell.styles.fillColor = [220, 230, 255];
                        hook.cell.styles.textColor = primaryColor;
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                    doc.setFontSize(8); doc.setTextColor(130);
                    doc.setFont('helvetica', 'italic');
                    doc.text('This is a system generated report', pageWidth / 2, pageHeight - 20, { align: 'center' });
                    doc.setFont('helvetica', 'normal');
                    doc.text('Developed and Maintained by Payserve', pageWidth / 2, pageHeight - 15, { align: 'center' });
                    doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
                }
            });

            const legendY = doc.lastAutoTable.finalY + 6;
            doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(120, 120, 120);
            doc.text(
                'Bal B/F = Credits − Debits up to end of previous month  |  Credits = top-ups collected in month  |  ' +
                'Debits = water consumption billed in month  |  Closing Bal = Bal B/F + Credits − Debits',
                14, legendY
            );
        }

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Facility_Consumption_Report_${timestamp}.pdf`);
        setShowConsumptionModal(false);
        toastify('Report exported successfully', 'success');
    };

    const handleExportExcel = async () => {
        const data = await fetchFacilityConsumption();
        if (!data || !data.monthlyTotals?.length) {
            toastify('No consumption data found for the selected months', 'warning');
            return;
        }

        const financialResponse = await fetchFinancialSummary();
        const financialData = financialResponse?.data;

        const { monthlyTotals, grandTotalConsumption, totalMeters } = data;
        const totalInitial = monthlyTotals.reduce((s, r) => s + r.totalInitialReading, 0);
        const totalFinal = monthlyTotals.reduce((s, r) => s + r.totalFinalReading, 0);

        const excelData = monthlyTotals.map((row, i) => ({
            'No.': i + 1,
            'Month': formatYearMonth(row.yearMonth),
            'Year-Month': row.yearMonth,
            'Meters Reporting': row.meterCount,
            'Initial Reading (m³)': parseFloat(row.totalInitialReading.toFixed(2)),
            'Final Reading (m³)': parseFloat(row.totalFinalReading.toFixed(2)),
            'Total Consumption (m³)': parseFloat(row.totalConsumption.toFixed(2)),
        }));
        excelData.push({
            'No.': '', 'Month': 'TOTAL', 'Year-Month': '', 'Meters Reporting': '',
            'Initial Reading (m³)': parseFloat(totalInitial.toFixed(2)),
            'Final Reading (m³)': parseFloat(totalFinal.toFixed(2)),
            'Total Consumption (m³)': parseFloat(grandTotalConsumption),
        });

        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 24 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Monthly Consumption');

        if (financialData && financialData.monthlySummary?.length) {
            const { monthlySummary } = financialData;
            const totals = monthlySummary.reduce((acc, row) => ({
                activeBF: acc.activeBF + (row.active?.balanceBF || 0),
                activeCredits: acc.activeCredits + (row.active?.creditsInMonth || 0),
                activeDebits: acc.activeDebits + (row.active?.debitsInMonth || 0),
                activeClosing: acc.activeClosing + (row.active?.closingBalance || 0),
                inactiveBF: acc.inactiveBF + (row.inactive?.balanceBF || 0),
                inactiveCredits: acc.inactiveCredits + (row.inactive?.creditsInMonth || 0),
                inactiveDebits: acc.inactiveDebits + (row.inactive?.debitsInMonth || 0),
                inactiveClosing: acc.inactiveClosing + (row.inactive?.closingBalance || 0),
                combinedBF: acc.combinedBF + (row.combined?.balanceBF || 0),
                combinedCredits: acc.combinedCredits + (row.combined?.creditsInMonth || 0),
                combinedDebits: acc.combinedDebits + (row.combined?.debitsInMonth || 0),
                combinedClosing: acc.combinedClosing + (row.combined?.closingBalance || 0),
            }), {
                activeBF: 0, activeCredits: 0, activeDebits: 0, activeClosing: 0,
                inactiveBF: 0, inactiveCredits: 0, inactiveDebits: 0, inactiveClosing: 0,
                combinedBF: 0, combinedCredits: 0, combinedDebits: 0, combinedClosing: 0,
            });

            const financialExcel = monthlySummary.map((row, i) => ({
                'No.': i + 1,
                'Month': formatYearMonth(row.yearMonth),
                'Year-Month': row.yearMonth,
                'Active Accounts': row.active?.accountCount || 0,
                'Active - Bal B/F (KES)': row.active?.balanceBF || 0,
                'Active - Credits (KES)': row.active?.creditsInMonth || 0,
                'Active - Debits (KES)': row.active?.debitsInMonth || 0,
                'Active - Closing Bal (KES)': row.active?.closingBalance || 0,
                'Inactive Accounts': row.inactive?.accountCount || 0,
                'Inactive - Bal B/F (KES)': row.inactive?.balanceBF || 0,
                'Inactive - Credits (KES)': row.inactive?.creditsInMonth || 0,
                'Inactive - Debits (KES)': row.inactive?.debitsInMonth || 0,
                'Inactive - Closing Bal (KES)': row.inactive?.closingBalance || 0,
                'Total - Bal B/F (KES)': row.combined?.balanceBF || 0,
                'Total - Credits (KES)': row.combined?.creditsInMonth || 0,
                'Total - Debits (KES)': row.combined?.debitsInMonth || 0,
                'Total - Closing Bal (KES)': row.combined?.closingBalance || 0,
            }));
            financialExcel.push({
                'No.': '', 'Month': 'TOTAL', 'Year-Month': '',
                'Active Accounts': financialData.totalActiveAccounts,
                'Active - Bal B/F (KES)': parseFloat(totals.activeBF.toFixed(2)),
                'Active - Credits (KES)': parseFloat(totals.activeCredits.toFixed(2)),
                'Active - Debits (KES)': parseFloat(totals.activeDebits.toFixed(2)),
                'Active - Closing Bal (KES)': parseFloat(totals.activeClosing.toFixed(2)),
                'Inactive Accounts': financialData.totalInactiveAccounts,
                'Inactive - Bal B/F (KES)': parseFloat(totals.inactiveBF.toFixed(2)),
                'Inactive - Credits (KES)': parseFloat(totals.inactiveCredits.toFixed(2)),
                'Inactive - Debits (KES)': parseFloat(totals.inactiveDebits.toFixed(2)),
                'Inactive - Closing Bal (KES)': parseFloat(totals.inactiveClosing.toFixed(2)),
                'Total - Bal B/F (KES)': parseFloat(totals.combinedBF.toFixed(2)),
                'Total - Credits (KES)': parseFloat(totals.combinedCredits.toFixed(2)),
                'Total - Debits (KES)': parseFloat(totals.combinedDebits.toFixed(2)),
                'Total - Closing Bal (KES)': parseFloat(totals.combinedClosing.toFixed(2)),
            });

            const ws2 = XLSX.utils.json_to_sheet(financialExcel);
            ws2['!cols'] = [
                { wch: 5 }, { wch: 20 }, { wch: 12 },
                { wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 24 },
                { wch: 18 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 26 },
                { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 24 },
            ];
            XLSX.utils.book_append_sheet(wb, ws2, 'Financial Summary');
        }

        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
            { 'Info': 'Facility Water Consumption & Financial Report' },
            { 'Info': `Generated: ${new Date().toLocaleString()}` },
            { 'Info': `Total Meters in Facility: ${totalMeters}` },
            { 'Info': `Grand Total Consumption: ${parseFloat(grandTotalConsumption).toFixed(2)} m³` },
            { 'Info': `Active Accounts: ${financialData?.totalActiveAccounts ?? 'N/A'}` },
            { 'Info': `Inactive Accounts: ${financialData?.totalInactiveAccounts ?? 'N/A'}` },
            { 'Info': 'Bal B/F = Credits − Debits up to end of previous month' },
            { 'Info': 'Credits = top-up payments collected in the month' },
            { 'Info': 'Debits = water consumption billed in the month' },
            { 'Info': 'Closing Bal = Bal B/F + Credits − Debits' },
        ]), 'Report Info');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Facility_Consumption_Report_${timestamp}.xlsx`);
        setShowConsumptionModal(false);
        toastify('Report exported successfully', 'success');
    };

    const handleExport = () => {
        if (consumptionFormat === 'pdf') handleExportPDF();
        else handleExportExcel();
    };

    const handleView = (rowData) => {
        navigate(`/facility/utility_management/meter_management/view_water_meter/${rowData._id}`);
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-eye p-mr-2"
                onClick={() => handleView(rowData)}
                tooltip="View Meter"
            />
        </div>
    );

    const waterStatusBodyTemplate = (rowData) => (
        <span className={`badge ${rowData.status === 'opened' ? 'bg-success text-light' : 'bg-danger text-light'}`}>
            {rowData.status}
        </span>
    );

    const installationStatusTemplate = (rowData) => {
        const statusColors = { installed: 'success', 'not installed': 'warning' };
        return (
            <span className={`badge bg-${statusColors[rowData.installationStatus] || 'secondary'}`}>
                {rowData.installationStatus}
            </span>
        );
    };

    const unitColumnTemplate = (rowData) => {
        const label = rowData.UnitInfo?.name || 'N/A';
        return (
            <div className="d-flex flex-column gap-1">
                <span>{label}</span>
            </div>
        );
    };

    const filteredMeters = meters.filter(meter => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            meter.meterNumber?.toString().toLowerCase().includes(q) ||
            meter.UnitInfo?.name?.toString().toLowerCase().includes(q) ||
            meter.meterCategory?.toLowerCase().includes(q)
        );
    });

    const recentMonths = getRecentMonths(24);

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <div className="row mb-3 align-items-center">
                        <div className="col-md-4 col-xs-12">
                            <input
                                className="form-control"
                                placeholder="Search by meter number, unit, floor or bulk..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-8 col-xs-12 d-flex justify-content-end gap-2">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setMetersExportFormat('pdf');
                                    setShowMetersExportModal(true);
                                }}
                            >
                                <i className="ti ti-list me-1"></i>
                                Export Meters List
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setSelectedMonths([]);
                                    setConsumptionFormat('pdf');
                                    setShowConsumptionModal(true);
                                }}
                            >
                                <i className="ti ti-chart-bar me-1"></i>
                                Export Consumption Report
                            </button>
                        </div>
                    </div>

                    <div className="dt-responsive table-responsive">
                        <DataTable
                            value={filteredMeters}
                            dataKey="_id"
                            emptyMessage="No water meters found."
                            sortMode="multiple"
                            paginator
                            rows={rows}
                            rowsPerPageOptions={rowsPerPageOptions}
                            stripedRows
                            loading={loading}
                            tableStyle={{ minWidth: '50rem' }}
                        >
                            <Column field="meterNumber" header="Meter S/N" sortable />
                            <Column header="Unit / Floor / Bulk" sortable sortField="UnitInfo.name" body={unitColumnTemplate} />
                            <Column field="status" header="Status" body={waterStatusBodyTemplate} sortable />
                            <Column field="protocol" header="Meter Protocol" sortable />
                            <Column field="previousReading" header="Previous V (m³)" sortable />
                            <Column field="currentReading" header="Current V (m³)" sortable />
                            <Column
                                field="updatedAt"
                                header="Last Updated"
                                sortable
                                body={(rowData) => rowData.updatedAt ? new Date(rowData.updatedAt).toLocaleString() : 'N/A'}
                            />
                            <Column field="installationStatus" header="Installation Status" body={installationStatusTemplate} sortable />
                            <Column header="Actions" body={actionBodyTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>

            {/* Export Meters List Modal */}
            {showMetersExportModal && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header" style={{ background: '#1e40af' }}>
                                <h5 className="modal-title text-white">
                                    <i className="ti ti-list me-2"></i>
                                    Export Meters List
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowMetersExportModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-4">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="metersExportFormat"
                                                id="mfmtPDF"
                                                value="pdf"
                                                checked={metersExportFormat === 'pdf'}
                                                onChange={(e) => setMetersExportFormat(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="mfmtPDF">
                                                <i className="ti ti-file-type-pdf text-danger me-1"></i>
                                                PDF Report
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="metersExportFormat"
                                                id="mfmtExcel"
                                                value="excel"
                                                checked={metersExportFormat === 'excel'}
                                                onChange={(e) => setMetersExportFormat(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="mfmtExcel">
                                                <i className="ti ti-file-type-xls text-success me-1"></i>
                                                Excel Spreadsheet
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    <div>
                                        <strong>This export includes:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li>Meter Serial Number</li>
                                            <li>Account Number</li>
                                            <li>Unit Name</li>
                                        </ul>
                                        <div className="mt-2">
                                            <span className="badge bg-primary me-1">{filteredMeters.length}</span>
                                            meter{filteredMeters.length !== 1 ? 's' : ''} will be exported
                                            {searchTerm && <span className="text-muted ms-1">(filtered by search)</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowMetersExportModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleExportMeters}
                                    disabled={metersExportLoading || filteredMeters.length === 0}
                                >
                                    {metersExportLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-download me-1"></i>
                                            Export {metersExportFormat === 'pdf' ? 'PDF' : 'Excel'}
                                            <span className="ms-1">({filteredMeters.length} meters)</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Consumption Report Modal (unchanged) */}
            {showConsumptionModal && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div className="modal-content">

                            <div className="modal-header" style={{ background: '#1e40af' }}>
                                <h5 className="modal-title text-white">
                                    <i className="ti ti-chart-bar me-2"></i>
                                    Facility Monthly Consumption Report
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowConsumptionModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-4">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="consumptionFormat"
                                                id="fmtPDF"
                                                value="pdf"
                                                checked={consumptionFormat === 'pdf'}
                                                onChange={(e) => setConsumptionFormat(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="fmtPDF">
                                                <i className="ti ti-file-type-pdf text-danger me-1"></i>
                                                PDF Report
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="consumptionFormat"
                                                id="fmtExcel"
                                                value="excel"
                                                checked={consumptionFormat === 'excel'}
                                                onChange={(e) => setConsumptionFormat(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="fmtExcel">
                                                <i className="ti ti-file-type-xls text-success me-1"></i>
                                                Excel Spreadsheet
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label fw-bold mb-0">
                                            Select Months
                                            <span className="text-danger ms-1">*</span>
                                            {selectedMonths.length > 0 && (
                                                <span className="badge bg-primary ms-2">
                                                    {selectedMonths.length} selected
                                                </span>
                                            )}
                                        </label>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setSelectedMonths(recentMonths.map(m => m.value))}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setSelectedMonths([])}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            maxHeight: '260px',
                                            overflowY: 'auto',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px',
                                            padding: '10px'
                                        }}
                                    >
                                        <div className="row g-2">
                                            {recentMonths.map((m) => {
                                                const isChecked = selectedMonths.includes(m.value);
                                                return (
                                                    <div className="col-md-4 col-6" key={m.value}>
                                                        <div
                                                            className={`p-2 rounded border d-flex align-items-center gap-2 ${isChecked
                                                                ? 'border-primary bg-primary bg-opacity-10'
                                                                : 'border-light bg-light'
                                                                }`}
                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            onClick={() => toggleMonth(m.value)}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input m-0 flex-shrink-0"
                                                                checked={isChecked}
                                                                onChange={() => toggleMonth(m.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <span style={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                                                                {m.label}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    <div>
                                        <strong>Report includes:</strong>
                                        <ol className="mb-0 mt-1">
                                            <li><strong>Water Consumption</strong> — total m³ consumed across all meters per selected month</li>
                                            <li><strong>Financial Summary</strong> — Balance B/F, Credits collected, Debits/utilization, and Closing Balance broken down by <span className="text-success fw-semibold">Active</span> vs <span className="text-danger fw-semibold">Inactive</span> accounts</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowConsumptionModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleExport}
                                    disabled={consumptionLoading || selectedMonths.length === 0}
                                >
                                    {consumptionLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-download me-1"></i>
                                            Export {consumptionFormat === 'pdf' ? 'PDF' : 'Excel'}
                                            {selectedMonths.length > 0 && (
                                                <span className="ms-1">
                                                    ({selectedMonths.length} month{selectedMonths.length > 1 ? 's' : ''})
                                                </span>
                                            )}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SmartMetersTab;